import React, { useState } from "react"
import { Container, Row, Col, Card, CardBody } from "reactstrap"
import { useQuery } from "react-query"
import WelcomeComp from "./WelcomeComp"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { GENERAL } from "api/general"
import Swal from "sweetalert2"
import StackedColumnChart from "./StackedColumnChart"
import PieChart2 from "./PieChart2"
import ProgressChart from "components/Progress"

// Read-only national summary dashboard — no downloads, no drill-down navigation
const NationalAlgonCoordinatorDashboard = () => {
  const [dashboardStats, setDashboardStats] = useState(null)
  const [statesWithHighestAgents, setStatesWithHighestAgents] = useState([])
  const [male, setMale] = useState(0)
  const [female, setFemale] = useState(0)
  const [approved, setApproved] = useState(0)
  const [rejected, setRejected] = useState(0)

  useQuery({
    queryKey: ["nationalAlgonCoordinatorDashboard"],
    queryFn: () => {
      Swal.fire({
        title: "Loading...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading(Swal.getDenyButton())
        },
      })
      return GENERAL.getDashboardStatistics()
    },
    onSuccess: res => {
      Swal.close()
      setDashboardStats(res)
      setMale(res?.applicationByGender?.male || 0)
      setFemale(res?.applicationByGender?.female || 0)
      setApproved(res?.applicationPercentages?.approved || 0)
      setRejected(res?.applicationPercentages?.rejected || 0)
    },
  })

  useQuery({
    queryKey: ["nationalAlgonCoordinatorStateData"],
    queryFn: () => GENERAL.getStatesWithHighestRegistrationAgents(),
    onSuccess: res => setStatesWithHighestAgents(res || []),
  })

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboards" breadcrumbItem="National Summary" />

          <Row>
            <Col xl="4">
              <WelcomeComp />
              <PieChart2
                dataColors='["--bs-primary", "--bs-success"]'
                periodData={[male, female]}
              />
              <Card style={{ height: 300 }}>
                <CardBody>
                  <h5 className="card-title mb-4">Application Overview</h5>
                  <ProgressChart value={approved} color="info" name="Approved" />
                  <ProgressChart value={rejected} color="warning" name="Rejected" />
                </CardBody>
              </Card>
            </Col>
            <Col xl="8">
              <Row>
                <Col md="4">
                  <Card className="mini-stats-wid">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <p className="text-muted fw-medium">Total Applicants</p>
                          <h4 className="mb-0 text-primary">
                            {dashboardStats?.totalApplicants || 0}
                          </h4>
                        </div>
                        <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                          <span className="avatar-title rounded-circle bg-primary">
                            <i className="bx bx-copy-alt font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="4">
                  <Card className="mini-stats-wid">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <p className="text-muted fw-medium">Approved Ward Registrars</p>
                          <h4 className="mb-0 text-success">
                            {dashboardStats?.totalApprovedAgents || 0}
                          </h4>
                        </div>
                        <div className="avatar-sm rounded-circle bg-success align-self-center mini-stat-icon">
                          <span className="avatar-title rounded-circle bg-success">
                            <i className="bx bx-check-double font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="4">
                  <Card className="mini-stats-wid">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <p className="text-muted fw-medium">Pending Ward Registrars</p>
                          <h4 className="mb-0 text-warning">
                            {dashboardStats?.totalPendingAgents || 0}
                          </h4>
                        </div>
                        <div className="avatar-sm rounded-circle bg-warning align-self-center mini-stat-icon">
                          <span className="avatar-title rounded-circle bg-warning">
                            <i className="bx bx-time font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="4">
                  <Card className="mini-stats-wid">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <p className="text-muted fw-medium">Declined Ward Registrars</p>
                          <h4 className="mb-0 text-danger">
                            {dashboardStats?.totalDeclinedAgents || 0}
                          </h4>
                        </div>
                        <div className="avatar-sm rounded-circle bg-danger align-self-center mini-stat-icon">
                          <span className="avatar-title rounded-circle bg-danger">
                            <i className="bx bx-x font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="4">
                  <Card className="mini-stats-wid">
                    <CardBody>
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <p className="text-muted fw-medium">Rejected Ward Registrars</p>
                          <h4 className="mb-0 text-danger">
                            {dashboardStats?.totalRejectedAgents || 0}
                          </h4>
                        </div>
                        <div className="avatar-sm rounded-circle bg-danger align-self-center mini-stat-icon">
                          <span className="avatar-title rounded-circle bg-danger">
                            <i className="bx bx-block font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              <Card>
                <CardBody>
                  <h5 className="card-title mb-4">States with Highest Registrations</h5>
                  <StackedColumnChart
                    periodData={statesWithHighestAgents}
                    dataColors='["--bs-primary", "--bs-primary", "--bs-primary"]'
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default NationalAlgonCoordinatorDashboard
